import { useEffect, useState } from 'react';
import { getOpenModalCount } from './useAnyModalOpen';

// Spots a newer deployment of the app. A shop-floor tablet can keep the page open for days,
// and the app never reloads itself, so it would keep running old code (a shelf count was
// skipped this way on 2026-10-15). Each build gives the main script a new hashed name, so
// comparing the one in the live index.html with the one running is enough.

const CHECK_EVERY_MS = 5 * 60 * 1000;
// Reload by itself once nobody has touched the page for this long, unless a dialog is open
// or a field has focus, so nothing being typed is lost.
export const IDLE_RELOAD_MS = 10 * 60 * 1000;

const MAIN_SCRIPT = /\/static\/js\/main\.[0-9a-f]+\.js/;

export const runningScript = () => {
  const script = Array.from(document.scripts).find(s => MAIN_SCRIPT.test(s.getAttribute('src') || ''));
  return script ? script.getAttribute('src').match(MAIN_SCRIPT)[0] : null;
};

export const fetchLiveScript = async () => {
  try {
    const response = await fetch('/index.html', { cache: 'no-store' });
    if (!response.ok) return null;
    const match = (await response.text()).match(MAIN_SCRIPT);
    return match ? match[0] : null;
  } catch {
    return null; // offline - try again next time
  }
};

const isBusy = () => {
  const active = document.activeElement;
  return getOpenModalCount() > 0
    || Boolean(active && active.matches('input, textarea, select, [contenteditable="true"]'));
};

const useAppUpdate = () => {
  const [updateReady, setUpdateReady] = useState(false);

  // Look for a new version now and then, and whenever the page comes back into view
  useEffect(() => {
    const running = runningScript();
    if (!running) return undefined; // dev server: no hashed bundle to compare
    let stopped = false;
    const check = async () => {
      const live = await fetchLiveScript();
      if (!stopped && live && live !== running) setUpdateReady(true);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, []);

  // Once one is ready, reload when the page has been left alone
  useEffect(() => {
    if (!updateReady) return undefined;
    let lastActivity = Date.now();
    const touch = () => { lastActivity = Date.now(); };
    const events = ['pointerdown', 'keydown', 'input', 'wheel', 'touchstart'];
    events.forEach(e => window.addEventListener(e, touch, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - lastActivity >= IDLE_RELOAD_MS && !isBusy()) window.location.reload();
    }, 30 * 1000);
    return () => {
      events.forEach(e => window.removeEventListener(e, touch));
      clearInterval(timer);
    };
  }, [updateReady]);

  return { updateReady, reload: () => window.location.reload() };
};

export default useAppUpdate;
